import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
} from 'rxjs/operators';

import { isBlipGoCartItem as isBlipGoCartItemRef } from '../constants/blip-go.constants';
import { CheckContractedProductsResponse } from '../models/product-v2.model';
import { CartItem } from '../store/cart/cart.state';
import { CartFacade } from '../store/cart/cart.facade';
import { UserFacade } from '../store/user/user.facade';
import { ToastService } from '../../shared/services/toast.service';
import { unformatCnpj } from '../utils/cnpj.util';

import { ProductV2Service } from './product-v2.service';

/**
 * No login (e ao trocar CNPJ), consulta produtos já contratados.
 * Se Blip Go estiver contratado: esconde o card e remove do carrinho.
 */
@Injectable({
  providedIn: 'root',
})
export class BlipGoContractService {
  private readonly destroy$ = new Subject<void>();
  private readonly hasContractedProductSubject = new BehaviorSubject<boolean>(false);
  private readonly lastCheckedDocumentSubject = new BehaviorSubject<string | null>(null);
  private readonly CONTRACTED_CART_CLEARED_TOAST =
    'Encontramos produto(s) já contratado(s) no carrinho. Esses itens foram removidos automaticamente.';

  private initialized = false;
  private isRemovingBlipGo = false;

  private readonly productV2Service = inject(ProductV2Service);
  private readonly userFacade = inject(UserFacade);
  private readonly cartFacade = inject(CartFacade);
  private readonly toastService = inject(ToastService);

  readonly hasContractedProduct$ = this.hasContractedProductSubject.asObservable();
  private platformId = inject(PLATFORM_ID);


  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialized) {
      return;
    }
    this.initialized = true;

    combineLatest([
      this.userFacade.getIsAuthenticated(),
      this.userFacade.getLoginData(),
      this.userFacade.getUserDetails(),
    ])
      .pipe(
        map(([isAuthenticated, loginData, user]) => {
          if (!isAuthenticated) {
            return null;
          }
          const document = loginData?.document || user?.document || null;
          const cleaned = document ? unformatCnpj(document) : '';
          return cleaned.length >= 11 ? cleaned : null;
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(document => {
        if (!document) {
          this.reset();
          return;
        }
        this.checkContractedProducts(document);
      });

    this.hasContractedProduct$
      .pipe(
        filter(hasContracted => hasContracted),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.removeBlipGoFromCart());

    this.cartFacade
      .getCartItems()
      .pipe(
        debounceTime(50),
        filter(() => this.shouldHideBlipGo()),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        if (items.some(item => this.isBlipGoCartItem(item))) {
          this.removeBlipGoFromCart();
        }
      });
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.initialized = false;
  }

  shouldHideBlipGo(): boolean {
    return this.hasContractedProductSubject.value;
  }

  isBlipGoCartItem(item: CartItem): boolean {
    return isBlipGoCartItemRef(item);
  }

  checkContractedProducts(document: string): void {
    const cleaned = unformatCnpj(document);
    if (!cleaned || cleaned.length < 11) {
      return;
    }

    if (this.lastCheckedDocumentSubject.value === cleaned) {
      return;
    }

    this.lastCheckedDocumentSubject.next(cleaned);

    this.productV2Service
      .checkContractedProducts(cleaned)
      .pipe(
        take(1),
        catchError(error => {
          console.error('Erro ao verificar produtos contratados (Blip Go):', error);
          return of({ hasContractedProduct: false } as CheckContractedProductsResponse);
        })
      )
      .subscribe(response => {
        const hasContracted = this.resolveHasContractedProduct(response);
        this.hasContractedProductSubject.next(hasContracted);
      });
  }

  reset(): void {
    this.hasContractedProductSubject.next(false);
    this.lastCheckedDocumentSubject.next(null);
  }

  private resolveHasContractedProduct(
    response: CheckContractedProductsResponse | null | undefined
  ): boolean {
    if (!response) {
      return false;
    }
    if (typeof response.hasContractedProduct === 'boolean') {
      return response.hasContractedProduct;
    }
    if (typeof response.data?.hasContractedProduct === 'boolean') {
      return response.data.hasContractedProduct;
    }
    return false;
  }

  private removeBlipGoFromCart(): void {
    if (this.isRemovingBlipGo) {
      return;
    }

    this.cartFacade
      .getCartItems()
      .pipe(take(1))
      .subscribe(items => {
        const blipItems = items.filter(item => this.isBlipGoCartItem(item));
        if (!blipItems.length) {
          return;
        }

        this.isRemovingBlipGo = true;
        blipItems.forEach(item => this.cartFacade.removeFromCart(item.id));
        this.showContractedCartClearedToast();

        setTimeout(() => {
          this.isRemovingBlipGo = false;
        }, 300);
      });
  }

  /** Toast amarelo ao limpar produtos já contratados do carrinho (ex.: Blip Go). */
  private showContractedCartClearedToast(): void {
    this.toastService.showWarning(this.CONTRACTED_CART_CLEARED_TOAST, 5000, 'top-center');
  }
}
